import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash-es';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { Application } from 'src/portaljs/constants/application';
import Icons from 'src/app/core/enums/icons.enum';
import { ConsoleLogger } from 'src/app/core/shared/console-logger';
import { MatMenuTrigger } from '@angular/material/menu';
import { environment } from 'src/environments/environment';
import { Subject, take, takeUntil } from 'rxjs';
import { Util } from 'src/app/core/shared/util';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { SessionService } from 'src/app/core/services/session.service';
import { DialogModeType } from 'src/app/core/shared/error/dialog-message-type.enum';
import { UserMessage } from 'src/app/core/shared/error/user-message';
import { ToastMessageService } from 'src/app/core/services/toast-message.service';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { DestinyViewType } from 'src/portaljs/constants/destiny-view-type';
import { ErrorDialogService } from 'src/app/core/services/error-dialog.service';
import { SidenavMenuStates } from 'src/app/core/enums/sidenav-menu-states.enum';

@Component({
    selector: 'fss-portal-secret',
    templateUrl: (environment.production ? '../empty.html' : './secret.component.html'),
    styleUrls: [(environment.production ? '../empty.html' : './secret.component.scss')],
    standalone: false
})
export class SecretComponent implements OnDestroy, OnInit {
  public icons = Icons;
  public apps = [Application.COLLECTIONS, Application.DD, Application.DASHBOARD, Application.FM, Application.CLM, Application.PSARI, Application.DESTINY_UI, Application.DESTINY_AI, Application.JUNIPER_SIDECAR];
  public useFakeGrants: boolean = localStorage.getItem('useFake') === 'true';
  public clearAndReload: boolean = false;
  public preserveIframes: boolean = localStorage.getItem('preserveIframes') === 'true';
  public showClm: boolean = localStorage.getItem('showClm') === 'true';
  public enableDebug: boolean = localStorage.getItem('portaljs_debug') === 'true';
  public disableUnV2: boolean = localStorage.getItem('fss_unv2_disabled') === 'true';
  public appVals: any = {};
  private ngUnsubscribe = new Subject();
  public sidenavMenuStates = Object.values(SidenavMenuStates) as SidenavMenuStates[];
  public activeMenuState: SidenavMenuStates;

  public setSidenavMenuState() {
    this.portalStateService.setMenuStateForApp(this.activeMenuState);
  }

  constructor(
    private envSvc: EnvironmentService,
    private router: Router,
    private authService: AuthService,
    private clientStateService: ClientStateService,
    private readonly toastService: ToastMessageService,
    private portalStateService: PortalStateService,
    private sessionService: SessionService,
    private readonly errorDialogService: ErrorDialogService
  ) {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line

    for (const app of this.apps) {
      const key = Util.getApplicationKey(app);
      this.appVals[key] = {
        url: localStorage.getItem(key + '-override') || '',
        hidden: localStorage.getItem(key + '-hidden')
      };
    }

    this.portalStateService.getMenuState$()
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe(state => {
        if (state) {
          const activeapp = this.portalStateService.getActiveApp().app;
          this.activeMenuState = state[activeapp]?.current;
        }
      });
  }

  public getAppKey(app: Application) {
    return Util.getApplicationKey(app);
  }

  public ngOnInit(): void {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
  }

  public save(menuTrigger: MatMenuTrigger) {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line

    for (const app of this.apps) {
      const key = Util.getApplicationKey(app);
      const val = this.appVals[key];

      if (val) {
        if (_.trim(val.url).length === 0) {
          this.envSvc.resetUrl(app);
          ConsoleLogger.debug('Removing url override for: ' + key);
          localStorage.removeItem(key + '-override' );
        } else {
          ConsoleLogger.debug('Adding url override for: ' + key + ' - ' + val);
          localStorage.setItem(key + '-override', val.url);
        }

        if (val.hidden) {
          ConsoleLogger.debug('Hiding: ' + key);
          localStorage.setItem(key + '-hidden', val.hidden);
        } else {
          ConsoleLogger.debug('Unhiding: ' + key);
          localStorage.removeItem(key + '-hidden' );
        }
      }
    }

    if (this.preserveIframes) {
      localStorage.setItem('preserveIframes', 'true');
    } else {
      localStorage.removeItem('preserveIframes');
    }

    if (this.showClm) {
      localStorage.setItem('showClm', 'true');
    } else {
      localStorage.removeItem('showClm');
    }

    if (this.enableDebug) {
      localStorage.setItem('portaljs_debug', 'true');
    } else {
      localStorage.removeItem('portaljs_debug');
    }

    if (this.disableUnV2) {
      localStorage.setItem('fss_unv2_disabled', 'true');
    } else {
      localStorage.removeItem('fss_unv2_disabled');
    }


    const preInfos = _.cloneDeep(this.envSvc.appInfos.filter(x => x.name !== Application.DESTINY)).sort();
    this.envSvc.loadAppInfos()
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe((infos) => {
        const newInfos = _.cloneDeep(infos.filter(x => x.name !== Application.DESTINY)).sort();
        if (newInfos.length !== preInfos.length || _.differenceBy(preInfos, newInfos, 'appId').length > 0) {
          window.location.reload();
        }
      });

    if (this.useFakeGrants) {
      ConsoleLogger.debug('Fake Grants enabled');
      localStorage.setItem('useFake', 'true');
    } else {
      ConsoleLogger.debug('Fake Grants removed');
      localStorage.removeItem('useFake');
    }


    if (this.clearAndReload) {
      this.clearAndGoToAaspSitePicker();
    } else {
      this.authService.setUserLogin(this.authService.getUserLogin());
      menuTrigger.closeMenu();
    }
  }

  public generateNotifications(event: any) {
    event.stopPropagation();
    let id = this.clientStateService.getNotifications().length;
    let newNotifs = [{id: `${id}`, title: `${id}`, description: 'description for notification ' + id, createdDate: new Date(), new: true}];
    id++;
    newNotifs = [{id: `${id}`, title: `${id}`, description: 'description for notification ' + id, createdDate: new Date(), new: false}, ...newNotifs];
    this.clientStateService.setNotifications(Application.DASHBOARD, [...this.clientStateService.getNotifications(), ...newNotifs]);
  }

  public getAppUrl(app: Application)   {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    const key = Util.getApplicationKey(app);
    return this.appVals[key].url;
  }

  public getAppVal(app: Application) {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    return this.appVals[Util.getApplicationKey(app)];
  }

  public updateUrl(app: Application, event: any) {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line

    const key = Util.getApplicationKey(app);
    const val: string = event.target.value;
    this.appVals[key].url = val;
  }

  public clearAndGoToAaspSitePicker() {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    this.authService.setDistrictAppId(null);
    this.authService.setSiteGuid(null);
    this.authService.logout()
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.authService.setUserLogin(undefined);
        this.router.navigate(['/']);
    });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public showToast() {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    this.toastService.show(DialogModeType.ERROR, UserMessage.genericFailureMsg);
  }

  public createAppMenu() {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    this.clientStateService.setMenuItems([this.portalStateService.getActiveApp().app], [
      { id: 'Home', default: true, name: 'Home', icon: 'tab1active.gif' },
      { id: 'TopLevelCatalog', name: 'Catalog', icon: 'tab2passive.gif' },
      { id: 'TopLevelCirculation', name: 'Circulation', icon: 'tab3passive.gif' },
      { id: 'TopLevelReports', name: 'Reports', icon: 'reporttabpassive.gif' },
      { id: 'TopLevelBackOffice', name: 'Admin', icon: 'tab4passive.gif' },
      { id: 'TopLevelMyInfo', name: 'My Info', icon: 'myinfotabpassive.gif' },
      { id: 'MoreMenu', name: 'More', subMenuItems: [{ id: 'HelpMenu', name: 'Help' }] }
    ]);
    this.clientStateService.setDestinyViewTypeVO({
      viewTypes: Object.values(DestinyViewType),
      selectedViewType: DestinyViewType.ALL
    });
    this.portalStateService.setActiveMenuItems();
  }

  public killSession() {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    this.sessionService.stopSession_DEVONLY();
  }
  public openErrorDialog() {
    if (this.envSvc.isProductionBuild()) return; 'PORTAL_TEST_CODE'; // eslint-disable-line
    this.errorDialogService.open(
      DialogModeType.ERROR,
      'Message title',
      'Error Code',
      'Message Description',
      'Error Message',
      true
    );
  }

  public toggleSidenav() {
    this.portalStateService.setSideNavEnabledForCurrentApp(!this.portalStateService.isUnEnabledForCurrentApp());
    if (!this.portalStateService.isUnEnabledForCurrentApp()) {
      this.portalStateService.setMenuStateForApp(SidenavMenuStates.HIDDEN);
    } else {
      this.portalStateService.setMenuStateForApp(SidenavMenuStates.STANDARD);
    }
  }

  public get showSidenav() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }
}
